import {
  Autowired,
  Component,
  type IFloatingFilter,
  RefSelector,
  type IFloatingFilterParams,
  type AgInputTextField,
  type ColumnModel,
} from '@ag-grid-community/core'

type ParentModel =
  | {
      type: string
      filterType: 'string'
      filter: string
    }
  | {
      type: 'inRange'
      filterType: 'date'
      dateFrom: string
      dateTo: string
      filter?: never
    }
  | {
      type: 'inRange'
      filterType: 'number'
      filter: string
      filterTo: string
    }

export class SimpleReadOnlyFloatingFilter
  extends Component
  implements IFloatingFilter
{
  private readonly eFloatingFilterText!: AgInputTextField
  private readonly columnModel!: ColumnModel

  private params!: IFloatingFilterParams
  constructor() {
    super(`
          <div class="ag-floating-filter-input ag-set-floating-filter-input" role="presentation">
              <ag-input-text-field ref="eFloatingFilterText"></ag-input-text-field>
          </div>
    `)
  }

  // this is a user component, and IComponent has "public destroy()" as part of the interface.
  // so we need to override destroy() just to make the method public.
  destroy(): void {
    super.destroy()
  }

  init(params: IFloatingFilterParams): void {
    this.params = params

    this.eFloatingFilterText
      .setDisabled(true)
      .addGuiEventListener('mousedown', (e) => {
        e.stopPropagation()

        this.params.parentFilterInstance((instance) => {
          requestAnimationFrame(() => {
            // there is no good way to know a popup is open or not, so we see if we can get the height
            const { height } =
              // @ts-ignore
              instance?.eFilterBody?.getBoundingClientRect() ?? {}

            // bubble up the event to the parent to hide the popup
            this.eFloatingFilterText.getGui()?.parentElement?.dispatchEvent(e)
            if (!height) {
              this.params.showParentFilter()
            }
          })
        })

        return false
      })

    this.setParams(params)
  }

  private setParams(params: IFloatingFilterParams): void {
    const displayName = this.columnModel.getDisplayNameForColumn(
      params.column,
      'header',
      true,
    )
    const translate = this.localeService.getLocaleTextFunc()

    this.eFloatingFilterText.setInputAriaLabel(
      `${displayName} ${translate('ariaFilterInput', 'Filter Input')}`,
    )
  }

  onParamsUpdated(params: IFloatingFilterParams): void {
    this.params = params
    this.setParams(params)
  }

  onParentModelChanged(parentModel: ParentModel | null) {
    this.eFloatingFilterText.setValue(
      getValueText(
        parentModel,
        (this.params.filterParams as { blankText?: string }).blankText,
      ),
    )
  }
}

function getValueText(
  parentModel: ParentModel | null,
  blankText: string = 'blank',
) {
  if (!parentModel) {
    return ''
  }

  if (parentModel.type === 'inRange') {
    if (parentModel.filterType === 'number') {
      return `${parentModel.filter} - ${parentModel.filterTo}`
    } else if (parentModel.filterType === 'date') {
      return `${parentModel.dateFrom} - ${parentModel.dateTo}`
    }
  } else if (parentModel.type === 'blank') {
    return `= ${blankText}`
  } else if (parentModel.type === 'notBlank') {
    return `≠ ${blankText}`
  }

  if (parentModel.filterType === 'date') {
    return `${getValuePrefix(parentModel)} ${parentModel.dateFrom}`
  }

  return `${getValuePrefix(parentModel)} ${parentModel.filter}`
}

function getValuePrefix(parentModel: ParentModel) {
  switch (parentModel.type) {
    case 'equals':
      return '='

    case 'notEqual':
      return '≠'

    case 'lessThan':
      return '<'

    case 'lessThanOrEqual':
      return '<='

    case 'greaterThan':
      return '>'

    case 'greaterThanOrEqual':
      return '>='
  }

  return ''
}

RefSelector('eFloatingFilterText')(
  SimpleReadOnlyFloatingFilter.prototype,
  'eFloatingFilterText',
)
Autowired('columnModel')(SimpleReadOnlyFloatingFilter.prototype, 'columnModel')
